<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col cols="12" sm="6" md="8">
        <div class="mb-4">{{ review.name }} {{ review.review_update_time | convertToTimezone(undefined, "YYYY-MM-DD (dd) HH:mm") }}</div>
        <div v-if="clinicUser" class="mt-2">{{ $t('ReviewDetailCard.doctor') }} {{ review.doctor_name }}</div>
        <div v-else>{{ $t('ReviewDetailCard.clinic') }} {{ review.clinic_name }}</div>
        <v-row v-if="review.treatment && review.treatment.length" no-gutters dense class="mt-2 flex-nowrap">
          <v-col cols="auto" class="align-self-center">{{ $t('ReviewDetailCard.treatment') }}</v-col>
          <v-col cols="auto" class="flex-shrink-1">
            <v-chip v-for="(item, i) in review.treatment" :key="i" color="primary" label outlined class="mr-1 mb-1">
              {{ item }}
            </v-chip>
          </v-col>
        </v-row>
        <v-row v-if="review.recommend && review.recommend.length" no-gutters dense class="mt-2 flex-nowrap"
               style="gap: 0 6px">
          <v-col cols="auto">
            <v-icon>mdi-emoticon-excited-outline</v-icon>
          </v-col>
          <v-col cols="auto" class="flex-shrink-1">
            <v-chip v-for="(item, i) in review.recommend" :key="i" color="primary" outlined class="mr-1 mb-1">
              {{ item }}
            </v-chip>
          </v-col>
        </v-row>
        <v-row v-if="review.negative && review.negative.length" no-gutters dense class="mt-2 flex-nowrap"
               style="gap: 0 6px">
          <v-col cols="auto">
            <v-icon>mdi-emoticon-sad-outline</v-icon>
          </v-col>
          <v-col cols="auto" class="flex-shrink-1">
            <v-chip v-for="(item, i) in review.negative" :key="i" color="deep-orange lighten-1" outlined
                    class="mr-1 mb-1">
              {{ item }}
            </v-chip>
          </v-col>
        </v-row>
        <v-row no-gutters dense class="my-2 flex-nowrap">
          <v-col cols="auto">{{ $t('ReviewDetailCard.reviewMessage') }}</v-col>
          <v-col cols="auto" class="text-pre-line flex-shrink-1">{{ review.message }}</v-col>
        </v-row>
        <v-row v-if="review.photo && review.photo.length" no-gutters dense class="mt-2">
          <v-col cols="12" class="d-flex flex-wrap" style="gap: 8px">
            <v-img
              v-for="(photo, i) in review.photo"
              :key="i"
              :src="photo"
              max-width="120"
              max-height="120"
              class="rounded cursor-pointer"
              cover
              @click="openPhotoDialog(photo)"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <v-row v-for="(rating, key, i) in review.star" :key="i" no-gutters dense
               class="flex-nowrap" :class="{'justify-center': $vuetify.breakpoint.smAndUp}" style="gap: 2px">
          <v-col cols="auto" class="align-self-center">{{ key }}</v-col>
          <v-col cols="auto">
            <v-rating
              half-increments
              readonly
              length="5"
              :value="ceilToPoint(rating)"
              dense
              color="yellow darken-2"
              class="ma-auto"
              style="line-height: 30px"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12">
        <v-row no-gutters dense class="my-2">
          <v-col cols="12">
            回覆：
            <span
              v-if="!review.response && review.ai_generate_response"
              class="red-6--text font-weight-medium"
            >(您的回覆尚未送出)</span>
          </v-col>
          <v-col cols="12" class="text-pre-line flex-shrink-1">
            <ResponseEditor
              ref="responseEditor"
              :editMode="editDoctorResponse"
              :response="review.response"
              :editingResponse="doctorResponse"
              :aiResponse="review.ai_generate_response"
              :aiGenerating="!!Number(review.ai_generating)"
              :updated_time="review.response_update_time"
              :userInfo="userInfo"
              :negativeReview="negativeReview"
              :reviewType="reviewType"
              @cancelEdit="editDoctorResponse = false"
              @editResponse="editResponse"
              @submitResponse="submitResponse"
              @generateResponse="generateResponse"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-dialog v-if="dialog" v-model="dialog" max-width="300">
      <v-card>
        <v-card-title>
          <template v-if="reviewType === 'dentCo'">{{ $t('ReviewDetailCard.replyFromLine') }}</template>
          <template v-if="reviewType === 'google'">您的回覆內容將傳送至 診所Google商家，點擊確認後送出。</template>
        </v-card-title>
        <v-card-actions>
          <v-spacer/>
          <v-btn text color="primary" @click="submit">{{ $t('ReviewDetailCard.confirm') }}</v-btn>
          <v-btn text @click="dialog = false">{{ $t('GENERAL.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <confirmDialog
      v-model="toggleConfirmDialog"
      title="您尚未修改回覆內容，若有生成新的AI建議回覆，請點擊「複製到回覆欄中」。"
      content=""
      confirmBtnColor="primary"
      confirmBtnText="返回編輯"
      cancel-btn-text=""
      maxWidth="420"
      @confirm="toggleConfirmDialog = false"
    />

    <v-dialog v-model="reviewDeletedDialog" max-width="420" persistent>
      <v-card>
        <v-card-title class="text-body-1">
          此評論已不存在於 Google 商家頁面，無法回覆
        </v-card-title>
        <v-card-text>
          是否要一併從此頁面移除這則評論？
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="reviewDeletedDialog = false">我要保留</v-btn>
          <v-btn text color="primary" @click="removeReview">確定移除</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="photoDialog" max-width="800">
      <v-card>
        <v-img :src="selectedPhoto" max-height="80vh" contain />
        <v-btn icon absolute top right @click="photoDialog = false" class="white">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { generateAiResponseAPI, reviewReplyAPI, removeGoogleReviewAPI } from "@/api";
import ResponseEditor from "@/components/review/ResponseEditor.vue";
import { ceilToPoint } from "@/utils/tool";
import ConfirmDialog from "@/components/utils/ConfirmDialog.vue";

export default {
  name: "ReviewDetailCard",

  components: {ConfirmDialog, ResponseEditor},

  props: {
    review: {
      type: Object,
      required: true
    },
    userInfo: {
      type: Object
    },
    reviewType: {
      type: String,
      default: 'dentCo'
    }
  },

  data() {
    return {
      editDoctorResponse: false,
      doctorResponse: null,
      dialog: false,
      negativeStar: 3,

      toggleConfirmDialog: false,
      reviewDeletedDialog: false,
      photoDialog: false,
      selectedPhoto: null
    }
  },

  computed: {
    ...mapGetters([
      'axiosParamData',
      'clinicUser'
    ]),
    negativeReview() {
      if (this.review.star) {
        return Object.values(this.review.star).some(v => v <= this.negativeStar)
      }

      // for (const key in this.review.star) {
      //   if (this.review.star[key] < 3) {
      //     return true
      //   }
      // }
      return true
    }
  },

  methods: {
    ceilToPoint,
    openPhotoDialog(photo) {
      this.selectedPhoto = photo
      this.photoDialog = true
    },
    editResponse(response) {
      this.doctorResponse = response
      this.editDoctorResponse = true
    },
    submitResponse(response) {
      if (this.review.response === response) {
        return this.toggleConfirmDialog = true
      }
      this.doctorResponse = response
      this.dialog = true
    },
    generateResponse() {
      const data = {
        id: this.review.id,
        ...this.axiosParamData
      }
      generateAiResponseAPI(data).then(() => {
        this.$refs.responseEditor.generateLoading = false
        this.$emit('refresh')
      }).catch(e => {
        if (e.response && e.response.data && e.response.data.messages) {
          this.$notify.error(e.response.data.messages)
        } else {
          this.$notify.error(this.$t('GENERAL.oops'))
          console.error(e)
        }
        this.$refs.responseEditor.generateLoading = false
      })
    },
    submit() {
      const data = {
        ...this.axiosParamData,
        id: this.review.id,
        text: this.doctorResponse,
      }
      reviewReplyAPI(data).then(() => {
        this.editDoctorResponse = false
        this.$emit('refresh')
      }).catch(e => {
        if (e.response && e.response.data) {
          if (e.response.data.error_code === 'REVIEW_DELETED') {
            this.reviewDeletedDialog = true
          } else if (e.response.data.messages) {
            this.$notify.error(e.response.data.messages)
          } else {
            this.$notify.error(this.$t('GENERAL.oops'))
            console.error(e)
          }
        } else {
          this.$notify.error(this.$t('GENERAL.oops'))
          console.error(e)
        }
      }).finally(() => {
        this.dialog = false
      })
    },
    removeReview() {
      removeGoogleReviewAPI(this.review.id, this.axiosParamData).then(() => {
        this.reviewDeletedDialog = false
        this.$emit('refresh')
      }).catch(e => {
        if (e.response && e.response.data && e.response.data.messages) {
          this.$notify.error(e.response.data.messages)
        } else {
          this.$notify.error(this.$t('GENERAL.oops'))
          console.error(e)
        }
        this.reviewDeletedDialog = false
      })
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>